import React, { useCallback, useEffect, useRef } from "react";

const NON_DRAGGABLE_SELECTOR = "button, a, input, select, textarea, [data-no-drag]";
const EXPLICIT_HANDLE_SELECTOR = "[data-modal-drag-handle]";

interface IDragBounds {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
}

interface IDragSession extends IDragBounds {
	pointerId: number;
	startX: number;
	startY: number;
	originX: number;
	originY: number;
}

const clamp = (value: number, min: number, max: number): number =>
	min > max ? min : Math.min(Math.max(value, min), max);

const isHeaderLike = (element: Element | null | undefined): boolean =>
	!!element && /header/i.test(typeof element.className === "string" ? element.className : "");

const isWithinDragHandle = (content: HTMLElement, target: HTMLElement): boolean => {
	const explicit = target.closest(EXPLICIT_HANDLE_SELECTOR);
	if (explicit) return content.contains(explicit);

	const header = content.firstElementChild?.firstElementChild;
	return isHeaderLike(header) && !!header?.contains(target);
};

export const useModalDrag = (isDraggable: boolean) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const positionRef = useRef({ x: 0, y: 0 });
	const sessionRef = useRef<IDragSession | null>(null);

	const applyTransform = useCallback(() => {
		const node = contentRef.current;
		if (!node) return;

		const { x, y } = positionRef.current;
		node.style.transform = x || y ? `translate3d(${x}px, ${y}px, 0)` : "";
	}, []);

	const getBounds = useCallback((): IDragBounds | null => {
		const node = contentRef.current;
		if (!node) return null;

		const rect = node.getBoundingClientRect();
		const baseLeft = rect.left - positionRef.current.x;
		const baseTop = rect.top - positionRef.current.y;

		return {
			minX: -baseLeft,
			maxX: window.innerWidth - rect.width - baseLeft,
			minY: -baseTop,
			maxY: window.innerHeight - rect.height - baseTop,
		};
	}, []);

	const resetPosition = useCallback(() => {
		sessionRef.current = null;
		positionRef.current = { x: 0, y: 0 };
		applyTransform();
	}, [applyTransform]);

	const handlePointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const content = contentRef.current;
			if (!content || event.button !== 0) return;

			const target = event.target as HTMLElement;
			if (target.closest(NON_DRAGGABLE_SELECTOR)) return;
			if (!isWithinDragHandle(content, target)) return;

			const bounds = getBounds();
			if (!bounds) return;

			sessionRef.current = {
				...bounds,
				pointerId: event.pointerId,
				startX: event.clientX,
				startY: event.clientY,
				originX: positionRef.current.x,
				originY: positionRef.current.y,
			};

			event.currentTarget.setPointerCapture(event.pointerId);
		},
		[getBounds],
	);

	const handlePointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const session = sessionRef.current;
			if (!session || session.pointerId !== event.pointerId) return;

			positionRef.current = {
				x: clamp(session.originX + event.clientX - session.startX, session.minX, session.maxX),
				y: clamp(session.originY + event.clientY - session.startY, session.minY, session.maxY),
			};
			applyTransform();
		},
		[applyTransform],
	);

	const handlePointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		const session = sessionRef.current;
		if (!session || session.pointerId !== event.pointerId) return;

		sessionRef.current = null;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
	}, []);

	useEffect(() => {
		if (!isDraggable) return;

		const handleResize = () => {
			const { x, y } = positionRef.current;
			if (!x && !y) return;

			const bounds = getBounds();
			if (!bounds) return;

			positionRef.current = { x: clamp(x, bounds.minX, bounds.maxX), y: clamp(y, bounds.minY, bounds.maxY) };
			applyTransform();
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [isDraggable, getBounds, applyTransform]);

	const dragProps = isDraggable
		? {
				onPointerDown: handlePointerDown,
				onPointerMove: handlePointerMove,
				onPointerUp: handlePointerEnd,
				onPointerCancel: handlePointerEnd,
				"data-modal-draggable": true,
			}
		: {};

	return { contentRef, dragProps, resetPosition };
};

export const ModalDragContext = React.createContext(false);

export const useIsModalDraggable = (): boolean => React.useContext(ModalDragContext);
